import * as React from 'react'
import { useQueryState } from 'nuqs'

import { useDataTable } from '@/components/data-table/data-table-provider'
import { useSheetRowNavigation } from '@/hooks/useSheetRowNavigation'
import { DETAIL_SHEET_CLASS } from '@/components/detailSheet'
import { Sheet, SheetContent, SheetDescription } from '@/components/ui/sheet'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { useClientSheetData } from '@/features/patients/hooks/useClientSheetData'
import type { PatientRecord } from '@/features/patients/types'
import { ClientSheetHeader } from './ClientSheetHeader'
import {
  DEFAULT_CLIENT_TAB,
  ClientSheetTabs,
  isClientTabKey,
  type ClientTabKey,
} from './ClientSheetTabs'
import { ClientActivityTab } from './tabs/ClientActivityTab'
import { ClientAppointmentsTab } from './tabs/ClientAppointmentsTab'
import { ClientClinicalTab } from './tabs/ClientClinicalTab'
import { ClientDocumentsTab } from './tabs/ClientDocumentsTab'
import { ClientIdentifiersTab } from './tabs/ClientIdentifiersTab'
import { ClientOverviewTab } from './tabs/ClientOverviewTab'

/** URL param carrying the sheet's active tab, alongside the `details` flag. */
const TAB_PARAM = 'clientTab'

/**
 * Client details sheet — renders the selected row (from the DataTable context
 * via the `sheetSlot` prop) into a tabbed detail view. The record's own fields
 * are on the row; history, activity and clinical data are fetched per client.
 *
 * Shared by the Clients list and a duty's Clients tab; both supply the same
 * row shape (`DutyClientRecord extends PatientRecord`).
 */
export function PatientsSheetWrapper() {
  const { table, rowSelection, isLoading } = useDataTable<
    PatientRecord,
    unknown
  >()

  const {
    selectedRowKey,
    selectedRow,
    prevId,
    nextId,
    onPrev,
    onNext,
    onOpenChange,
  } = useSheetRowNavigation(table, rowSelection, !!isLoading)

  const patient = selectedRow?.original
  const { history, activity, clinical, tabs, counts } =
    useClientSheetData(patient)
  // "Today" for next-appointment and date labels. Fixed for the sheet's
  // lifetime: the boundary only moves at midnight.
  const [now] = React.useState(() => new Date())

  // Tab lives in the URL so a shared link lands on the same view, and so
  // ↑/↓ row navigation keeps the reader on the tab they were reading.
  const [rawTab, setRawTab] = useQueryState(TAB_PARAM, { history: 'replace' })
  const activeTab: ClientTabKey =
    // A tab this viewer may not open falls back to Overview.
    isClientTabKey(rawTab) && tabs.includes(rawTab)
      ? rawTab
      : DEFAULT_CLIENT_TAB

  const handleTabChange = React.useCallback(
    (next: string) => {
      if (!isClientTabKey(next)) return
      void setRawTab(next === DEFAULT_CLIENT_TAB ? null : next)
    },
    [setRawTab]
  )

  // Closing the sheet must clear the tab param too, or it lingers in the URL
  // and reopens the next client on a tab nobody chose.
  const handleOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (!isOpen) void setRawTab(null)
      onOpenChange(isOpen)
    },
    [onOpenChange, setRawTab]
  )

  const showIdentifiers = React.useCallback(
    () => handleTabChange('identifiers'),
    [handleTabChange]
  )

  return (
    <Sheet open={!!selectedRowKey} onOpenChange={handleOpenChange}>
      <SheetContent
        onCloseAutoFocus={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Reserve the scrollbar's width even when nothing scrolls: tabs differ
        // in length, and a scrollbar appearing on one and not the next would
        // change the content width and re-wrap the header on every switch.
        className={cn(
          DETAIL_SHEET_CLASS,
          'overflow-y-auto [scrollbar-gutter:stable]'
        )}
        showCloseButton={false}>
        <SheetDescription className="sr-only">
          Selected client details
        </SheetDescription>

        {patient ? (
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="gap-0">
            {/* Identity and tabs stick together: the tab strip pinned on its
                own would need the header's measured height, and the two read
                as one control anyway. */}
            <div className="bg-neutral-ui-bg border-neutral-subtle-border sticky top-0 z-10 border-b">
              <ClientSheetHeader
                patient={patient}
                prevId={prevId}
                nextId={nextId}
                onPrev={onPrev}
                onNext={onNext}
                onShowIdentifiers={showIdentifiers}
              />
              <div className="px-4">
                <ClientSheetTabs tabs={tabs} counts={counts} />
              </div>
            </div>

            {/* `flex-col` is load-bearing: TabsContent turns itself into a
                flex container when active, and in the default row direction a
                single child sizes to its content — leaving the cards short of
                the sheet's width. */}
            <div className="p-4">
              {/* forceMount: the care-gaps card's ↑/↓ year selection is its
                  own state, and a trip to Identifiers must not reset it. */}
              <TabsContent value="overview" forceMount className="flex-col">
                <ClientOverviewTab
                  patient={patient}
                  history={history}
                  activity={activity}
                  now={now}
                />
              </TabsContent>
              <TabsContent value="activity" className="flex-col">
                {/* Keyed by client so ↑/↓ resets the filter and paging. */}
                <ClientActivityTab
                  key={patient.id}
                  activity={activity}
                  history={history}
                  now={now}
                />
              </TabsContent>
              <TabsContent value="appointments" className="flex-col">
                <ClientAppointmentsTab history={history} now={now} />
              </TabsContent>
              <TabsContent value="clinical" className="flex-col">
                <ClientClinicalTab history={history} clinical={clinical} />
              </TabsContent>
              {tabs.includes('documents') && (
                <TabsContent value="documents" className="flex-col">
                  <ClientDocumentsTab key={patient.id} memberId={patient.id} />
                </TabsContent>
              )}
              <TabsContent value="identifiers" className="flex-col">
                <ClientIdentifiersTab patient={patient} />
              </TabsContent>
            </div>
          </Tabs>
        ) : isLoading ? (
          <p className="text-muted-foreground py-8 text-center text-sm">
            Loading…
          </p>
        ) : (
          <p className="text-muted-foreground py-8 text-center text-sm">
            Select a row to view details
          </p>
        )}
      </SheetContent>
    </Sheet>
  )
}
